import type { SoketiEvent } from '#compositions/useSoketi'
import { useSoketi } from '#compositions/useSoketi'
import { useLocalStorage } from '@vueuse/core'
import { DateTime } from 'luxon'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import type { AxiosError } from 'axios'
import { useReturnRulesReadingsLatest } from './returnRulesReadingsLatest'
import { useUserStore } from './user'
import type { CachedTerminalReturnRulesInterface } from '~/models/returnRules'
import type {
  ContainerType,
  EmptyReturnRulesCheckedEvent,
  EmptyReturnRulesReadingEvent,
  EmptyReturnRulesUpdatedEvent,
  ShippingLine,
  TerminalName,
  TerminalReadingRules,
} from '~/services/apiClient'
import { useGlobalFilters } from '~/compositions/globalFilters'
import useStartOfToday from '~/compositions/useStartOfToday'
import {
  makeShiftKey,
  ReturnRule,
  SpecificReturnRule,
} from '~/models/returnRules'
import {
  EmptyReturnRulesApi,
  EventName,
  Shift,
  WebsocketChannel,
} from '~/services/apiClient'
import { handleSkippedAPIError } from '~/apiErrorHandling'
import { isRequestAbort } from '~/requestAbort'
import { isNoHttpResponse, retryOnNoResponse } from '~/noResponseRetry'
import { reportWarning } from '~/observability/warnings'
import { fastCompareISO, fastParseISO } from '~/utils'

const RETURN_RULES_WRITE_LOCK = 'return-rules-write'
const RETURN_RULES_SYNC_LOCK = 'return-rules-sync'
const SYNC_RETRY_DELAYS_MS = [250, 500]

function isTransientSyncFailure(error: unknown): boolean {
  if (isNoHttpResponse(error)) {
    return true
  }
  const status = (error as AxiosError).response?.status
  return status === 502 || status === 503 || status === 504
}

function reportSyncFailure(error: unknown, attempts: number) {
  const err = error as AxiosError
  const status = err?.response?.status ?? 0
  const online = typeof navigator === 'undefined' ? true : navigator.onLine
  // Laptop sleep and a dropped wifi are the browser, not the API.
  if (!online && status === 0) {
    return
  }
  reportWarning('Empty return rules sync failed', {
    attempts,
    code: err?.code,
    status,
    online,
    message: typeof err?.message === 'string' ? err.message : undefined,
  })
}

export interface CategoryKey {
  terminal: TerminalName
  shippingLine: ShippingLine
  containerType: ContainerType
}
export interface CategoryKeyWithTimestamps extends CategoryKey {
  lastChecked: DateTime
  lastChanged: DateTime
}
type ShiftLookup = Map<string, ReturnRule>

type RulesLookup = Map<
  TerminalName,
  Map<ShippingLine, Map<ContainerType, ShiftLookup>>
>
type ExpandedRulesLookup = Map<
  ShippingLine,
  Map<ContainerType, Map<TerminalName, SpecificReturnRule[]>>
>
let globalIsInitializing = false
let globalInitPromise: Promise<void> | null = null
// Define the store once at module level
const useReturnRulesLatestStore = defineStore(
  'empty-return-rules-latest',
  () => {
    const loadingPromise = ref(undefined as Promise<void> | undefined)
    const isInitialized = ref(false)
    const isInUpdateRules = ref(false)
    const watchEnabled = ref(false)
    let initialLoadCompleted = false
    let recomputationScheduled = false
    let isRecomputing = false
    let lastCacheHash = ''
    // Persistent store
    const rulesByTerminalCache = useLocalStorage<
      CachedTerminalReturnRulesInterface[]
    >('empty_return_rules_by_terminal', [], {
      listenToStorageChanges: true,
      serializer: {
        read: (
          value: string | undefined
        ): CachedTerminalReturnRulesInterface[] => {
          if (value) {
            try {
              const rules: CachedTerminalReturnRulesInterface[] =
                JSON.parse(value)
              // Filter out values without rules. This will fix things for any customers
              // who were stuck in a "bad" state, see DEV-52
              return rules.filter((rules) => rules.rules)
            } catch (error) {
              console.error(
                'Error parsing empty return rules from local storage:',
                error
              )
              return []
            }
          }
          return []
        },
        write: (value: CachedTerminalReturnRulesInterface[]): string => {
          return JSON.stringify(value)
        },
      },
      onError: (e) => {
        console.error('Error loading empty return rules from local storage', e)
        localStorage.removeItem('empty_return_rules_by_terminal')
      },
    })
    // Data structures derived from rules and maintained by the `updateRefs` method
    const rules = ref<ReturnRule[]>([])
    const rulesLookup = ref(new Map() as RulesLookup)
    const expandedRules = ref(new Map() as ExpandedRulesLookup)
    const categoryKeys = ref([] as CategoryKeyWithTimestamps[])
    const notClosedCategoryKeys = ref([] as CategoryKeyWithTimestamps[])
    const latestReturnRuleDate = ref(DateTime.now())
    const startOfToday = useStartOfToday()

    const globalFilters = useGlobalFilters()
    const terminalMatchesPortComplexes =
      globalFilters.terminalMatchesPortComplexes
    let userStore: ReturnType<typeof useUserStore> | null = null
    let soketi: ReturnType<typeof useSoketi> | null = null

    // Computed properties
    const datesWithRules = computed(() => {
      const dates = []
      const maxDate = DateTime.max(
        latestReturnRuleDate.value,
        // Ensure we display at least three days of rules. Relevant when the rules
        // used to compute latestReturnRuleDate were not date-based
        startOfToday.value.plus({ days: 3 })
      )
      let date = startOfToday.value
      while (date < maxDate) {
        dates.push(date)
        date = date.plus({ days: 1 })
      }
      return dates
    })
    function computeCacheHash(
      cache: CachedTerminalReturnRulesInterface[]
    ): string {
      return cache
        .map(
          (t) =>
            `${t.terminal}:${t.rules?.length || 0}:${t.reading_last_observed}`
        )
        .join('|')
    }
    function recomputeRulesFromCache() {
      if (isRecomputing) {
        return
      }
      isRecomputing = true
      try {
        const rulesByKey = new Map<string, ReturnRule>()
        for (const terminalRules of rulesByTerminalCache.value) {
          if (!terminalRules.rules) continue
          for (const rawRule of terminalRules.rules) {
            // Just ignore rules that don't have a mapped shipping line or container type
            if (!rawRule.parsed_shipping_line) {
              continue
            }
            if (!rawRule.parsed_container_type) {
              continue
            }
            const rule = new ReturnRule(rawRule)
            rulesByKey.set(rule.key, rule)
          }
        }
        rules.value = Array.from(rulesByKey.values())
        lastCacheHash = computeCacheHash(rulesByTerminalCache.value)
      } finally {
        isRecomputing = false
      }
    }
    const loading = computed(() => !!loadingPromise.value)

    // Watches
    watch(
      rules,
      (newRules) => {
        updateRefs(newRules)
      },
      { flush: 'post' }
    )
    watch(
      rulesByTerminalCache,
      () => {
        if (isInUpdateRules.value) {
          return
        }
        // Prevent watch from firing during initialization
        if (
          globalIsInitializing ||
          globalInitPromise ||
          !watchEnabled.value ||
          !initialLoadCompleted
        ) {
          return
        }
        const currentHash = computeCacheHash(rulesByTerminalCache.value)
        if (currentHash === lastCacheHash) {
          return
        }
        if (recomputationScheduled) {
          return
        }
        recomputationScheduled = true
        lastCacheHash = currentHash
        // Use nextTick to batch all callbacks in the same tick
        nextTick(() => {
          const finalHash = computeCacheHash(rulesByTerminalCache.value)
          if (finalHash !== lastCacheHash) {
            // Hash changed, another callback already handled it
            recomputationScheduled = false
            return
          }
          recomputeRulesFromCache()
          recomputationScheduled = false
        })
      },
      { flush: 'post' }
    )

    // Functions
    async function updateRules(terminalRuleSets: TerminalReadingRules[]) {
      isInUpdateRules.value = true
      try {
        await navigator.locks.request(
          RETURN_RULES_WRITE_LOCK,
          { mode: 'exclusive' },
          async () => {
            const byTerminal = new Map<
              TerminalName,
              CachedTerminalReturnRulesInterface
            >()
            for (const rules of rulesByTerminalCache.value) {
              if (!userStore?.activatedTerminals.has(rules.terminal)) {
                continue
              }
              byTerminal.set(rules.terminal, rules)
            }
            for (const potentiallyCachedRules of terminalRuleSets) {
              if (
                !userStore?.activatedTerminals.has(
                  potentiallyCachedRules.terminal
                )
              ) {
                continue
              }
              if (!potentiallyCachedRules.rules) {
                updateRulesLastCheckedInternal({
                  terminal: potentiallyCachedRules.terminal,
                  checked_time: fastParseISO(
                    potentiallyCachedRules.reading_last_observed
                  ),
                  group_first_observed: fastParseISO(
                    potentiallyCachedRules.reading_first_observed
                  ),
                  targetArray: Array.from(byTerminal.values()),
                })
                continue
              }
              const rules =
                potentiallyCachedRules as CachedTerminalReturnRulesInterface
              const existingRules = byTerminal.get(rules.terminal)
              if (
                !existingRules ||
                existingRules.reading_first_observed <
                  rules.reading_first_observed ||
                existingRules.reading_last_parsed < rules.reading_last_parsed
              ) {
                byTerminal.set(rules.terminal, rules)
              } else {
                updateRulesLastCheckedInternal({
                  terminal: rules.terminal,
                  checked_time: fastParseISO(rules.reading_last_observed),
                  group_first_observed: fastParseISO(
                    rules.reading_first_observed
                  ),
                  targetArray: Array.from(byTerminal.values()),
                })
              }
            }
            // Reset flag before setting value so watch can fire
            isInUpdateRules.value = false
            const finalArray = Array.from(byTerminal.values())
            const newHash = computeCacheHash(finalArray)
            const hashChanged = newHash !== lastCacheHash
            lastCacheHash = newHash
            rulesByTerminalCache.value = finalArray
            // Recompute if data changed (watch will skip due to hash match)
            if (hashChanged) {
              recomputeRulesFromCache()
            }
          }
        )
      } finally {
        isInUpdateRules.value = false
      }
    }

    function updateRulesLastCheckedCore({
      terminal,
      checked_time_iso,
      group_first_observed_iso,
      targetArray,
    }: {
      terminal: TerminalName
      checked_time_iso: string
      group_first_observed_iso: string
      targetArray?: CachedTerminalReturnRulesInterface[]
    }): void {
      let shouldUpdateInMemoryRules = false
      const arrayToMutate = targetArray ?? rulesByTerminalCache.value

      for (const terminalRules of arrayToMutate) {
        if (terminalRules.terminal !== terminal) continue
        if (!terminalRules.rules) continue
        if (
          !fastCompareISO(
            terminalRules.reading_first_observed,
            group_first_observed_iso
          )
        ) {
          continue
        }
        terminalRules.reading_last_observed = checked_time_iso
        for (const rule of terminalRules.rules) {
          rule.group_last_observed = checked_time_iso
        }
        shouldUpdateInMemoryRules = true
        break
      }

      // Also update the already-materialized ReturnRule instances so lookups/ UI
      // reflect the latest "last observed" time without needing to rebuild all rules.
      if (!shouldUpdateInMemoryRules) return
      const checkedTime = fastParseISO(checked_time_iso)
      const groupFirstObserved = fastParseISO(group_first_observed_iso)
      const groupFirstObservedMillis = groupFirstObserved.toMillis()
      for (const rule of rules.value) {
        if (rule.terminal !== terminal) continue
        if (rule.first_observed.toMillis() !== groupFirstObservedMillis)
          continue
        rule.last_observed = checkedTime
      }
      updateCategoryKeysFromRules(rules.value)
    }

    function updateRulesLastCheckedInternal({
      terminal,
      checked_time,
      group_first_observed,
      targetArray,
    }: {
      terminal: TerminalName
      checked_time: DateTime
      group_first_observed: DateTime
      targetArray?: CachedTerminalReturnRulesInterface[]
    }): void {
      updateRulesLastCheckedCore({
        terminal,
        checked_time_iso: checked_time.toISO(),
        group_first_observed_iso: group_first_observed.toISO(),
        targetArray,
      })
    }

    async function updateRulesLastChecked(params: {
      terminal: TerminalName
      checked_time: DateTime
      group_first_observed: DateTime
    }): Promise<void> {
      await navigator.locks.request(
        RETURN_RULES_WRITE_LOCK,
        { mode: 'exclusive' },
        async () => {
          updateRulesLastCheckedInternal(params)
        }
      )
    }

    function updateRefs(rules: ReturnRule[]) {
      // Gather vars
      const lookup = new Map() as RulesLookup
      let maxDate = DateTime.local()
      // Iterate over rules
      for (const rule of rules) {
        // Build lookup tree
        if (!lookup.has(rule.terminal)) {
          lookup.set(rule.terminal, new Map())
        }
        let byTerminal = lookup.get(rule.terminal)
        if (!byTerminal) {
          byTerminal = new Map()
          lookup.set(rule.terminal, byTerminal)
        }
        let byShippingLine = byTerminal.get(rule.shipping_line)
        if (!byShippingLine) {
          byShippingLine = new Map()
          byTerminal.set(rule.shipping_line, byShippingLine)
        }
        let byContainerType = byShippingLine.get(rule.container_type)
        if (!byContainerType) {
          byContainerType = new Map()
          byShippingLine.set(rule.container_type, byContainerType)
        }
        // Set rule
        byContainerType.set(rule.shift_key, rule)
        if (rule.date) {
          maxDate = DateTime.max(maxDate, rule.date)
        }
      }
      rulesLookup.value = lookup
      latestReturnRuleDate.value = maxDate
      expandedRules.value = expandRules(lookup)
      updateCategoryKeysFromRules(rules)
    }

    function updateCategoryKeysFromRules(rules: ReturnRule[]) {
      const byCategory = new Map<string, CategoryKeyWithTimestamps>()
      const notClosedKeys = new Set<string>()

      for (const rule of rules) {
        let entry = byCategory.get(rule.categoryKey)
        if (!entry) {
          entry = {
            ...rule.category,
            lastChecked: rule.last_observed,
            lastChanged: rule.first_observed,
          }
          byCategory.set(rule.categoryKey, entry)
        } else {
          if (rule.last_observed > entry.lastChecked) {
            entry.lastChecked = rule.last_observed
          }
          if (rule.first_observed > entry.lastChanged) {
            entry.lastChanged = rule.first_observed
          }
        }

        if (!rule.closed) {
          notClosedKeys.add(rule.categoryKey)
        }
      }

      const sorted = Array.from(byCategory.values()).sort((a, b) => {
        const shippingLineCompare = a.shippingLine.localeCompare(b.shippingLine)
        if (shippingLineCompare !== 0) return shippingLineCompare
        const terminalCompare = a.terminal.localeCompare(b.terminal)
        if (terminalCompare !== 0) return terminalCompare
        return a.containerType.localeCompare(b.containerType)
      })

      categoryKeys.value = sorted
      notClosedCategoryKeys.value = sorted.filter((cat) => {
        const key = `${cat.shippingLine}-${cat.terminal}-${cat.containerType}`
        return notClosedKeys.has(key)
      })
    }

    /**
     * Expand rules into one rule per date+shift, per terminal/line/type
     * The way we receive rules they can be "global" or by date or by shift an addition
     * to for specific shifts, which is a bit difficult to work with
     * @param lookup The rules lookup
     * @returns The expanded rules lookup
     */
    function expandRules(lookup: RulesLookup): ExpandedRulesLookup {
      const expandedLookup = new Map() as ExpandedRulesLookup
      for (const [terminal, byShippingLine] of lookup.entries()) {
        for (const [
          shippingLine,
          byContainerType,
        ] of byShippingLine.entries()) {
          let expandedByContainerType = expandedLookup.get(shippingLine)
          if (!expandedByContainerType) {
            expandedByContainerType = new Map()
            expandedLookup.set(shippingLine, expandedByContainerType)
          }
          for (const containerType of byContainerType.keys()) {
            let expandedByTerminal = expandedByContainerType.get(containerType)
            if (!expandedByTerminal) {
              expandedByTerminal = new Map()
              expandedByContainerType.set(containerType, expandedByTerminal)
            }
            const terminalRules: SpecificReturnRule[] = []
            expandedByTerminal.set(terminal, terminalRules)
            for (const date of datesWithRules.value) {
              for (const shift of [Shift.First, Shift.Second]) {
                const rule = lookupRule(
                  shippingLine,
                  containerType,
                  terminal,
                  date,
                  shift
                )
                const specificRule = new SpecificReturnRule(
                  date,
                  shift,
                  terminal,
                  shippingLine,
                  containerType,
                  rule
                )
                terminalRules.push(specificRule)
              }
            }
          }
        }
      }
      return expandedLookup
    }
    function syncRules(): Promise<void> {
      // Deduplicate within this tab
      if (loadingPromise.value) {
        return loadingPromise.value
      }

      // Try to become the sync leader across all tabs via Web Locks API
      const promise = navigator.locks
        .request(
          RETURN_RULES_SYNC_LOCK,
          { mode: 'exclusive', ifAvailable: true },
          async (lock) => {
            if (!lock) {
              // Another tab is syncing - skip API call
              // We'll receive updates via listenToStorageChanges
              return
            }

            // This tab is the leader - perform the sync
            const readings_in_cache = rulesByTerminalCache.value
              // Make extra sure that what we send to the server actually has rules
              .filter((rule) => !!rule.rules)
              .map((rule) => ({
                terminal: rule.terminal,
                reading_last_parsed: rule.reading_last_parsed,
                reading_first_observed: rule.reading_first_observed,
              }))
            const api = new EmptyReturnRulesApi()
            let attempts = 0
            let resp
            try {
              resp = await retryOnNoResponse(
                () => {
                  attempts += 1
                  return api.getLatestRulesIfChangedEmptyReturnRulesRulesLatestIfChangedPost(
                    {
                      // Let the API know what we already have locally in cache
                      readings_in_cache,
                    },
                    undefined,
                    undefined,
                    { skipErrorHandling: true }
                  )
                },
                {
                  extraAttempts: SYNC_RETRY_DELAYS_MS.length,
                  delayMs: (attempt) => SYNC_RETRY_DELAYS_MS[attempt],
                  shouldRetry: isTransientSyncFailure,
                }
              )
            } catch (error) {
              // The skip above also held back the 401 logout. Run it for a 401
              // only; every other failure stays quiet for the user.
              if (
                (error as AxiosError).response?.status === 401 &&
                (await handleSkippedAPIError(error))
              ) {
                return
              }
              // Wake/reconnect often drops this cache refresh before any HTTP
              // answer, which the retries above absorb. Whatever is left stays
              // quiet for the user: the next reconnect or rules event syncs
              // again.
              if (!isRequestAbort(error)) {
                reportSyncFailure(error, attempts)
              }
              return
            }
            await updateRules(resp.data)
          }
        )
        // Flatten Promise<Promise<void>> to Promise<void> - Web Locks API awaits inner promise at runtime
        .then(() => {})
        .finally(() => {
          loadingPromise.value = undefined
        })

      loadingPromise.value = promise
      return promise
    }

    function getRuleForShift(
      byShift: Map<string, ReturnRule>,
      date: DateTime,
      shift: Shift
    ): ReturnRule | null {
      // By date+shift
      let shiftKey = makeShiftKey(date, shift)
      let rule = byShift.get(shiftKey)
      if (rule) return rule
      // By no date or shift
      shiftKey = makeShiftKey(null, null)
      rule = byShift.get(shiftKey)
      if (rule) return rule
      // By shift
      shiftKey = makeShiftKey(null, shift)
      rule = byShift.get(shiftKey)
      if (rule) return rule
      // By date
      // Not sure this case happens right now, but leaving in case we have a terminal
      // provide rules by date without shift
      shiftKey = makeShiftKey(date, null)
      rule = byShift.get(shiftKey)
      return rule ?? null
    }

    function lookupRule(
      shippingLine: ShippingLine,
      containerType: ContainerType,
      terminal: TerminalName,
      date: DateTime,
      shift: Shift
    ): ReturnRule | null {
      const byShippingLine = rulesLookup.value.get(terminal)
      if (!byShippingLine) return null
      const byContainerType = byShippingLine.get(shippingLine)
      if (!byContainerType) return null
      const byShift = byContainerType.get(containerType)
      if (!byShift) return null
      return getRuleForShift(byShift, date, shift)
    }

    function lookupRulesForTerminal(
      terminal: TerminalName,
      date: DateTime,
      shift: Shift
    ): ReturnRule[] {
      const byShippingLine = rulesLookup.value.get(terminal)
      if (!byShippingLine) return []
      const rules: ReturnRule[] = []
      for (const byContainerType of byShippingLine.values()) {
        for (const byShift of byContainerType.values()) {
          const rule = getRuleForShift(byShift, date, shift)
          if (rule) rules.push(rule)
        }
      }
      return rules
    }

    function soketiEventToAPIFormat(
      event: EmptyReturnRulesUpdatedEvent
    ): TerminalReadingRules {
      return {
        terminal: event.terminal,
        reading_first_observed: event.reading_first_observed,
        reading_last_parsed: event.reading_last_parsed,
        // Kinda a hack, should probably add a reading_last_observed to the event format
        reading_last_observed: event.rules[0].group_last_observed,
        rules: event.rules,
        // Doesn't really apply here, we know this is not a cached value
        cache_valid: false,
      }
    }

    function initialize(syncOnInitialization: boolean) {
      // If already initialized, return immediately
      if (isInitialized.value) {
        return
      }
      if (globalIsInitializing) {
        return
      }
      globalIsInitializing = true
      globalInitPromise = Promise.resolve().then(async () => {
        try {
          if (isInitialized.value) {
            return
          }
          isInitialized.value = true

          userStore = useUserStore()
          soketi = useSoketi()

          // Set up websocket event handlers
          async function rulesUpdatedEventHandler(event: SoketiEvent) {
            const update_event = event as EmptyReturnRulesUpdatedEvent
            await updateRules([soketiEventToAPIFormat(update_event)])
          }

          async function rulesCheckedEventHandler(event: SoketiEvent) {
            const check_event = event as EmptyReturnRulesCheckedEvent
            if (!terminalMatchesPortComplexes(check_event.terminal)) {
              return
            }
            const returnRulesReadingsLatestStore =
              useReturnRulesReadingsLatest()
            await updateRulesLastChecked({
              checked_time: fastParseISO(check_event.check_time),
              group_first_observed: fastParseISO(check_event.first_observed),
              terminal: check_event.terminal,
            })
            returnRulesReadingsLatestStore.updateObservedTimesFromSoketi(
              check_event
            )
          }

          function newReadingHandler(event: SoketiEvent) {
            const readingEvent = event as EmptyReturnRulesReadingEvent
            if (!terminalMatchesPortComplexes(readingEvent.reading.terminal)) {
              return
            }
            const reading = readingEvent.reading
            const returnRulesReadingsLatestStore =
              useReturnRulesReadingsLatest()
            returnRulesReadingsLatestStore.setReading(reading)
          }

          soketi.bindOnReconnect(syncRules)
          soketi.bind(
            WebsocketChannel.PrivateEmptyReturnRules,
            EventName.EmptyReturnRulesUpdated,
            rulesUpdatedEventHandler
          )
          soketi.bind(
            WebsocketChannel.PrivateEmptyReturnRules,
            EventName.EmptyReturnRulesReadingUpdated,
            newReadingHandler
          )
          soketi.bind(
            WebsocketChannel.PrivateEmptyReturnRuleChecks,
            EventName.EmptyReturnRulesChecked,
            rulesCheckedEventHandler
          )

          // Set hash before recomputing to prevent watch from firing
          lastCacheHash = computeCacheHash(rulesByTerminalCache.value)
          recomputeRulesFromCache()
          initialLoadCompleted = true

          if (syncOnInitialization) {
            loadingPromise.value = undefined
            await syncRules()
            watchEnabled.value = true
          } else {
            watchEnabled.value = true
            loadingPromise.value = undefined
          }
        } finally {
          globalInitPromise = null
          globalIsInitializing = false
        }
      })
      loadingPromise.value = globalInitPromise
    }

    const forTesting = {
      rules,
      rulesLookup,
      updateRules,
      updateRulesLastChecked,
      syncRules,
      rulesByTerminalCache,
    }
    return {
      loading,
      loadingPromise,
      datesWithRules,
      categoryKeys,
      notClosedCategoryKeys,
      expandedRules,
      lookupRule,
      lookupRulesForTerminal,
      initialize,
      // "Internal" methods and data structures are exported for testing purposes ONLY
      forTesting,
    }
  }
)

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useReturnRulesLatestStore, import.meta.hot)
  )
}

// Export a composable that initializes the store and returns it
export function useReturnRulesLatest(syncOnInitialization = true) {
  const store = useReturnRulesLatestStore()
  store.initialize(syncOnInitialization)
  return store
}
